/**
 * Canonical proactive-notification wire envelope — the single shape the producer
 * (conversation, Phase 2) emits and the channel consumers (whatsapp-integration
 * in Phase 1; Slack/Teams/email in v1.1) validate before any send (D-04).
 *
 * This is a cross-service wire contract, NOT a persisted Mongo document, so it
 * carries no `_schemaVersion` (D-01, same style as `involuntary_change_row` and
 * the `AgentsEvent` wire).
 *
 * The envelope carries NO deeplink (PNR-07). The proactive template is body-only —
 * the Meta dynamic URL button is incompatible with the encoded deeplink, and the
 * redirect moved to the reactive flow (PNR-08).
 *
 * `correlationId` is the STABLE end-to-end idempotency token (CR-01). The bus is
 * at-least-once: the producer re-publishes this envelope on every SQS redelivery
 * of the originating agents-reply, and the notification path persists NO outbound
 * Mongo doc on the conversation side (PNR-04), so the only dedup fence is this
 * token. The channel consumer (whatsapp-integration) keys its send-marker on
 * `correlationId` to collapse at-least-once delivery to at-most-once at the Meta
 * template send. It is the same `AgentsReply.correlationId` the conversation
 * outbox uses for the text path (D-19).
 *
 * `channelMetadata` carries the channel-specific routing address some channels
 * need to dispatch the send (Slack needs `teamId` to reach a workspace + an
 * optional `channelId` for the DM). whatsapp ignores it (it addresses by `to`
 * phone + a single business `phoneNumberId` from env). It is OPTIONAL because the
 * proactive flow is cold by nature: a channel with no cached routing address
 * (e.g. a slack traveler who never messaged) simply has no `teamId` and the
 * channel consumer drops the send (slack-integration `missing_team_id`). The
 * producer inherits it from the conversation's latest inbound routing blob.
 *
 * `voucherPdfs` is the minimal projection of each emitted voucher the
 * `trip_update` e-mail prints as a PDF (the e-mail connector renders it). A
 * channel that cannot attach simply ignores it.
 */
import { z } from 'zod';

import { NotificationChannelSchema } from '../customer_api/notification_channel';
import { VoucherPdfListSchema } from './voucher_pdf';
import { NotificationTypeSchema } from './types';

export const OutboundNotificationSchema = z.object({
  kind: z.literal('notification'),
  notificationType: NotificationTypeSchema,
  channel: NotificationChannelSchema,
  language: z.string().min(1),
  to: z.string().min(1),
  vars: z.record(z.string(), z.string()),
  conversationId: z.string().min(1),
  correlationId: z.string().min(1),
  channelMetadata: z
    .object({
      teamId: z.string().optional(),
      channelId: z.string().optional(),
    })
    .optional(),
  voucherPdfs: VoucherPdfListSchema.optional(),
});

export type OutboundNotification = z.infer<typeof OutboundNotificationSchema>;
